<template>
  <div class="reply-input-container" v-if="user">
    <div class="d-flex align-start">
      <div class="reply-avatar-wrapper mt-1">
        <v-avatar size="42" color="surface-variant">
          <v-img v-if="userProfile?.avatar" :src="userProfile.avatar"></v-img>
          <v-icon v-else icon="mdi-account-circle"></v-icon>
        </v-avatar>
      </div>
      <div class="flex-grow-1">
        <v-textarea
          ref="textareaRef"
          v-model="text"
          placeholder="Write your reply..."
          variant="outlined"
          auto-grow
          rows="1"
          max-rows="10"
          counter="300"
          :maxlength="300"
          hide-details="auto"
          class="reply-textarea mb-3"
          :loading="sending"
          :disabled="sending"
          @keydown.ctrl.enter="handleSend"
          @paste="handlePaste"
          @input="handleMentionInput"
        ></v-textarea>

        <!-- Mention Suggestions Dropdown -->
        <MentionSuggestions
          v-if="mentionState.visible"
          :query="mentionState.query"
          :position="{ top: mentionState.top, left: mentionState.left }"
          @select="insertMention"
          @close="mentionState.visible = false"
        />

        <!-- Media Preview -->
        <div v-if="mediaPreviews.length > 0" class="media-preview-container mb-3 d-flex flex-wrap gap-2">
          <div v-for="(preview, index) in mediaPreviews" :key="index" class="preview-item position-relative">
            <v-img
              :src="preview.url"
              width="100"
              height="100"
              cover
              class="rounded-lg border border-white-05"
            ></v-img>
            <v-btn
              icon="mdi-close"
              size="x-small"
              color="black"
              class="remove-btn"
              @click="removeMedia(index)"
              :disabled="sending"
            ></v-btn>
          </div>
        </div>
        
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3 text-caption"
        >
          {{ error }}
        </v-alert>

        <div class="d-flex justify-space-between align-center">
          <div class="d-flex align-center">
            <input
              type="file"
              ref="fileInput"
              class="d-none"
              accept="image/*"
              multiple
              @change="onFileSelected"
            />
            <v-btn
              icon="mdi-image-outline"
              variant="text"
              color="primary"
              size="small"
              @click="fileInput?.click()"
              :disabled="sending || mediaPreviews.length >= 4"
              title="Add Images"
            ></v-btn>
            <v-menu :close-on-content-click="false" location="bottom start">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon="mdi-emoticon-outline"
                  variant="text"
                  color="primary"
                  size="small"
                  :disabled="sending"
                  title="Add Emoji"
                ></v-btn>
              </template>
              <v-card class="rounded-lg shadow-xl border border-white-05 bg-surface-light" width="auto">
                <Picker 
                  :data="emojiIndex" 
                  set="twitter"                   
                  @select="addEmoji" 
                  theme="dark"
                />
              </v-card>
            </v-menu>
          </div>

          <div class="d-flex align-center">
            <span class="text-caption text-grey mr-4 d-none d-sm-inline">Ctrl + Enter to send</span>
            <div class="hint-spacer"></div>
            <v-btn
              color="primary"
              class="font-weight-bold rounded-pill px-6"
              :loading="sending"
              :disabled="(!text.trim() && mediaFiles.length === 0) || sending"
              @click="handleSend"
            >
              Reply
            </v-btn>
            <v-btn
              variant="text"
              class="ml-2 font-weight-bold"
              @click="$emit('cancel')"
              :disabled="sending"
            >
              Cancel
            </v-btn>
          </div>
        </div>
      </div>
    </div>
  </div>
  <v-alert v-else type="info" variant="tonal" class="ma-4">
    Login to reply to this post.
  </v-alert>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { sendReply, getCurrentUser, userProfile, uploadBlob } from '@/services/bluesky';
import data from "emoji-mart-vue-fast/data/all.json";
import "emoji-mart-vue-fast/css/emoji-mart.css";
import { Picker, EmojiIndex } from "emoji-mart-vue-fast/src";
import MentionSuggestions from './MentionSuggestions.vue';

// // Fix for Vue 3 warning crash by adding toString to the prototype
// if (EmojiIndex && EmojiIndex.prototype) {
//   if (!(EmojiIndex.prototype as any).toString || (EmojiIndex.prototype as any).toString === Object.prototype.toString) {
//     Object.defineProperty(EmojiIndex.prototype, 'toString', { value: () => '[EmojiIndex]', enumerable: false, configurable: true });
//   }
//   if (!(EmojiIndex.prototype as any)[Symbol.toPrimitive]) {
//     Object.defineProperty(EmojiIndex.prototype, Symbol.toPrimitive, { value: () => '[EmojiIndex]', enumerable: false, configurable: true });
//   }
// }

const props = defineProps<{
  root: { uri: string; cid: string };
  parent: { uri: string; cid: string };
}>();

const emit = defineEmits(['success', 'cancel']);

const emojiIndex = new EmojiIndex(data);

const text = ref('');
const sending = ref(false);
const error = ref('');
const user = ref(getCurrentUser());
const fileInput = ref<HTMLInputElement | null>(null);
const mediaFiles = ref<File[]>([]);
const mediaPreviews = ref<any[]>([]);
const textareaRef = ref<any>(null);

const mentionState = ref({
  visible: false,
  query: '',
  top: 0,
  left: 0,
  cursorPos: 0
});

const handleMentionInput = (e: any) => {
  const el = e.target;
  const value = el.value;
  const cursorPos = el.selectionStart;
  
  const textBefore = value.substring(0, cursorPos);
  const lastAtMatch = textBefore.match(/(?:^|\s)@([a-zA-Z0-9.-]*)$/);
  
  if (lastAtMatch) {
    const rect = el.getBoundingClientRect();
    mentionState.value = {
      visible: true,
      query: lastAtMatch[1],
      top: rect.top + 40,
      left: rect.left + 20,
      cursorPos: cursorPos
    };
  } else {
    mentionState.value.visible = false;
  }
};

const insertMention = (user: any) => {
  const textBefore = text.value.substring(0, mentionState.value.cursorPos);
  const lastAtPos = textBefore.lastIndexOf('@');
  const beforeAt = textBefore.substring(0, lastAtPos);
  const textAfter = text.value.substring(mentionState.value.cursorPos);
  
  text.value = beforeAt + `@${user.handle} ` + textAfter;
  mentionState.value.visible = false;
  
  setTimeout(() => {
    if (textareaRef.value) {
      const textarea = textareaRef.value.$el.querySelector('textarea');
      if (textarea) {
        textarea.focus();
        const newPos = beforeAt.length + user.handle.length + 2; 
        textarea.selectionStart = textarea.selectionEnd = newPos;
      }
    }
  }, 50);
};

const handleFiles = (files: FileList | File[]) => {
  const fileArray = Array.from(files);
  for (const file of fileArray) {
    if (mediaFiles.value.length >= 4) break;
    if (!file.type.startsWith('image/')) continue;
    
    mediaFiles.value.push(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      mediaPreviews.value.push({
        url: e.target?.result,
        type: 'image'
      });
    };
    reader.readAsDataURL(file);
  }
};

const onFileSelected = (event: any) => {
  const files = event.target.files;
  if (!files) return;
  handleFiles(files);
  if (fileInput.value) fileInput.value.value = '';
};

const handlePaste = (event: ClipboardEvent) => {
  const items = event.clipboardData?.items;
  if (!items) return;
  
  const files: File[] = [];
  for (let i = 0; i < items.length; i++) {
    if (items[i].kind === 'file') {
      const file = items[i].getAsFile();
      if (file) files.push(file);
    }
  }
  
  if (files.length > 0) {
    handleFiles(files);
  }
};

const removeMedia = (index: number) => {
  mediaFiles.value.splice(index, 1);
  mediaPreviews.value.splice(index, 1);
};

const addEmoji = (emoji: any) => {
  text.value += emoji.native;
};

const handleSend = async () => {
  if ((!text.value.trim() && mediaFiles.value.length === 0) || sending.value) return;

  sending.value = true;
  error.value = '';

  try {
    let embed = undefined;
    if (mediaFiles.value.length > 0) {
      const blobs = await Promise.all(
        mediaFiles.value.map(file => uploadBlob(file, file.type))
      );
      embed = {
        $type: 'app.bsky.embed.images',
        images: blobs.map((blob, i) => ({
          image: blob,
          alt: `Image ${i + 1}`
        }))
      };
    }

    await sendReply(text.value, props.root, props.parent, embed);
    text.value = '';
    mediaFiles.value = [];
    mediaPreviews.value = [];
    emit('success');
  } catch (err: any) {
    error.value = err.message || 'Failed to send reply. Please try again.';
  } finally {
    sending.value = false;
  }
};
</script>

<style scoped>
.reply-input-container {
  background-color: #0d0d0d !important;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  max-width: 800px;
  margin: 0 auto;
  padding: 20px 24px !important;
}

.reply-avatar-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50px;
  flex-shrink: 0;
  position: relative;
  margin-right: 20px;
}

.hint-spacer {
  width: 20px;
}

.reply-textarea :deep(.v-field) {
  border-radius: 12px !important;
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.reply-textarea :deep(.v-field--focused) {
  border-color: #3b82f6 !important;
}

.media-preview-container {
  gap: 8px;
}

.preview-item {
  width: 100px;
  height: 100px;
}

.remove-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.6) !important;
  color: white !important;
}
</style>
